<template>
  <div v-if="applicant" id="applicant-edit-tab-personal-data">
    <div class="vx-row">
      <div class="vx-col md:w-1/2 w-full">
        <vs-table
          v-if="applicantDocumentsPage"
          :data="applicantDocumentsPage.results"
        >
          <template slot="header">
            <h3>Dokumente</h3>
          </template>
          <template slot="thead">
            <vs-th>Name</vs-th>
            <vs-th>Datum</vs-th>
            <vs-th>Aktion</vs-th>
            <vs-td v-if="$store.getters['user/hasPermission']('cv_parsing')"
              >Lebenslaufanalyse</vs-td
            >
            <vs-th />
            <vs-th></vs-th>
          </template>

          <template slot-scope="{ data }">
            <vs-tr v-for="entry in data" :key="entry.id" :data="entry">
              <vs-td>
                {{ entry.title }}
              </vs-td>

              <vs-td>
                {{ $d(new Date(entry.createdAt), "long") }}
              </vs-td>

              <vs-td>
                <vs-button @click="download(entry.data)">Download</vs-button>
              </vs-td>

              <vs-td v-if="$store.getters['user/hasPermission']('cv_parsing')">
                <template v-if="entry.analysisStarted">
                  <template v-if="entry.data.analysis">
                    Abgeschlossen
                  </template>
                  <template v-else>Wird verarbeitet</template>
                </template>
                <vs-button v-else @click="analyseRecord(entry.id)"
                  >Analysieren</vs-button
                >
              </vs-td>
              <vs-td>
                <feather-icon
                  icon="trash"
                  svg-classes="h-5 w-5 hover:text-danger cursor-pointer"
                  @click="deleteRecord(entry.id)"
                />
              </vs-td>
            </vs-tr>
          </template>
        </vs-table>
      </div>
      <form class="vx-col md:w-1/2 w-full" @submit.prevent="upload">
        <DataInput
          v-model="form.title"
          class="w-full mb-4 pr-3"
          label="Bezeichnung"
          name="title"
        />
        <div>
          <input
            type="file"
            name="file"
            :disabled="uploading"
            class="mb-4"
            @change="
              filesChange($event.target.name, $event.target.files);
              fileCount = $event.target.files.length;
            "
          />
        </div>

        <vs-button :disabled="uploading || !formData" @click="upload"
          >Hochladen</vs-button
        >
      </form>
    </div>
  </div>
</template>

<script>
import DataInput from "@/components/ui/DataInput";
import gql from "graphql-tag";
import { saveAs } from "file-saver";
import debug from "../../../helpers/debug";

export default {
  components: {
    DataInput
  },
  data() {
    document.setDebugCookie = value =>
      debug.setCookie("promatch_debug_mode", value, 100);
    return {
      form: {
        title: ""
      },
      formData: null,
      uploading: false,
      notifier: null
    };
  },
  emits: ["cv-analysis-started", "cv-analysis-complete"],
  apollo: {
    applicant: {
      query: gql`
        query Applicant($id: ID!) {
          applicant(id: $id) {
            id
          }
        }
      `,
      variables() {
        return {
          id: this.$route.params.id
        };
      }
    },
    applicantDocumentsPage: {
      query: gql`
        query($applicant: ID!, $page: Int) {
          applicantDocumentsPage(applicant: $applicant, page: $page) {
            total
            results {
              id
              title
              createdAt
              analysisStarted
              cvDataSynced
              data {
                id
                name
                size
                analysis
              }
            }
          }
        }
      `,
      fetchPolicy: "network-only",
      variables() {
        return {
          applicant: this.$route.params.id,
          page: 1
        };
      }
    }
  },
  watch: {
    applicantDocumentsPage: {
      deep: true,
      handler(page) {
        if (!page || !page.results) {
          return;
        }
        let cvAnalysisStarted = false;
        let cvAnalysisComplete = false;
        if (this.notifier !== null) {
          window.clearInterval(this.notifier);
        }
        for (let result of page.results) {
          if (result.data.analysis && !result.cvDataSynced) {
            let analysisResults = JSON.parse(result.data.analysis);
            analysisResults.documentID = result.id;
            cvAnalysisComplete = true;
            this.$emit("cv-analysis-complete", analysisResults);
          } else if (result.analysisStarted && !result.data.analysis) {
            cvAnalysisStarted = true;
          }
        }
        if (cvAnalysisStarted && !cvAnalysisComplete) {
          let $this = this;
          this.notifier = window.setInterval(function() {
            if (cvAnalysisStarted && !cvAnalysisComplete) {
              $this.$apollo.queries.applicantDocumentsPage.refetch();
            }
          }, 10000);
        }
        this.$emit("cv-analysis-started", cvAnalysisStarted);
      }
    }
  },
  methods: {
    filesChange(fieldName, fileList) {
      // handle file changes
      const formData = new FormData();

      if (!fileList.length) {
        this.formData = null;
        return;
      }

      // append the files to FormData
      formData.append(fieldName, fileList[0], fileList[0].name);

      // save it
      this.formData = formData;
      if (!this.form.title) {
        this.form.title = fileList[0].name;
      }
    },
    async analyseRecord(id) {
      await this.$apollo.mutate({
        mutation: gql`
          mutation($id: ID!) {
            analyseApplicantDocument(id: $id) {
              id
            }
          }
        `,
        variables: {
          id
        }
      });
      this.$apollo.queries.applicantDocumentsPage.refresh();
    },
    async download(file) {
      const { data } = await this.$axiosStorageApi({
        url: `/file/${file.id}/download_auth`,
        method: "GET",
        responseType: "blob"
      });

      saveAs(data, file.name);
    },
    async upload() {
      if (!this.formData) {
        // TODO: Display "no upload set" message
        return;
      }
      this.uploading = true;
      let data;

      try {
        const response = await this.$axiosStorageApi.post(
          "/file/upload_auth",
          this.formData
        );
        data = response.data;
      } catch (e) {
        // TODO: Display error message
      }

      this.$apollo
        .mutate({
          mutation: gql`
            mutation($applicant: ID!, $input: ApplicantDocumentInput!) {
              createApplicantDocument(applicant: $applicant, input: $input) {
                id
              }
            }
          `,
          variables: {
            applicant: this.applicant.id,
            input: {
              title: this.form.title,
              data: {
                id: data.id,
                size: data.size,
                type: data.type,
                name: data.name,
                content: data.content
              }
            }
          }
        })
        .then(() => {
          this.$apollo.queries.applicantDocumentsPage.refresh();
          this.form.title = "";
        });

      this.uploading = false;
    },
    deleteRecord(id) {
      this.$vs.dialog({
        type: "confirm",
        color: "danger",
        title: "Löschen bestätigen",
        text: `Du bist dabei, ein Dokument zu löschen. Möchtest du diese wirklich löschen?`,
        accept: () => {
          this.$apollo
            .mutate({
              mutation: gql`
                mutation($id: ID!) {
                  deleteApplicantDocument(id: $id) {
                    id
                  }
                }
              `,
              variables: {
                id: id
              }
            })
            .then(() => {
              this.$apollo.queries.applicantDocumentsPage.refresh();
            });
        },
        acceptText: "Löschen",
        cancelText: "Abbrechen"
      });
    }
  }
};
</script>
